import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Card } from "@/components/ui/card";

interface FAQItem {
  question: string;
  answer: string;
}

interface FAQProps {
  title?: string;
  subtitle?: string;
  items: FAQItem[];
  className?: string;
  /** FAQPage JSON-LD is emitted only on the dedicated /faq page (owner SEO rule). */
  emitSchema?: boolean;
}

export const FAQ = ({
  title = "Frequently Asked Questions",
  subtitle = "Find answers to common questions about our luxury smart toilet systems",
  items,
  className = "",
  emitSchema = false
}: FAQProps) => {
  return (
    <section className={`py-16 sm:py-20 lg:py-24 px-4 sm:px-6 lg:px-12 ${className}`}>
      <div className="container mx-auto max-w-4xl">
        <div className="text-center mb-12 fade-in-observer">
          <h2 className="font-playfair text-3xl sm:text-4xl lg:text-5xl font-bold text-primary mb-4">
            {title}
          </h2>
          <p className="font-inter text-lg text-muted-foreground max-w-2xl mx-auto">
            {subtitle}
          </p>
        </div>

        <Card className="border-border/20 shadow-elegant fade-in-observer">
          <Accordion type="single" collapsible className="w-full">
            {items.map((item, index) => (
              <AccordionItem key={index} value={`item-${index}`} className="border-border/20">
                <AccordionTrigger className="font-inter text-left px-6 py-4 hover:text-accent hover:no-underline transition-colors">
                  <span className="font-medium">{item.question}</span>
                </AccordionTrigger>
                <AccordionContent className="font-inter text-muted-foreground px-6 pb-4 leading-relaxed">
                  {item.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Card>

        {/* Structured Data for SEO — only when explicitly enabled (the /faq page emits its own). */}
        {emitSchema && (
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              "mainEntity": items.map(item => ({
                "@type": "Question",
                "name": item.question,
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": item.answer
                }
              }))
            })}
          </script>
        )}
      </div>
    </section>
  );
};

export default FAQ;
